import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';

const twinkle = keyframes`
  0% { opacity: 0.5; }
  50% { opacity: 1; }
  100% { opacity: 0.5; }
`;

const float = keyframes`
  0% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(3deg); }
  100% { transform: translateY(0px) rotate(0deg); }
`;

const shootingStarAnimation = keyframes`
  0% { transform: translateX(0) translateY(0); opacity: 1; }
  70% { opacity: 1; }
  100% { transform: translateX(-300px) translateY(300px); opacity: 0; }
`;

/* New asteroid movement animation */
const asteroidMovement = keyframes`
  0% {
    transform: translateX(100vw) rotate(90deg);
  }
  100% {
    transform: translateX(-100vw) rotate(-90deg);
  }
`;

const SpaceContainer = styled.div`
  width: 100vw;
  height: 100vh;
  background: linear-gradient(to bottom, #0f0c29, #302b63, #24243e);
  overflow: hidden;
  position: relative;
`;

const Star = styled.div`
  position: absolute;
  background: #fff;
  border-radius: 50%;
  animation: ${twinkle} ${props => props.duration}s infinite;
  transition: transform 0.3s ease-out;
`;

const Planet = styled.div`
  width: 150px;
  height: 150px;
  border-radius: 50%;
  background: 
    radial-gradient(circle at 30% 30%, 
      rgba(156, 179, 158, 1) 0%, 
      rgba(136, 159, 138, 1) 30%, 
      rgba(116, 139, 118, 1) 60%, 
      rgba(96, 119, 98, 1) 100%);
  position: absolute;
  top: 30%;
  left: 20%; /* Centers the planet horizontally */
  transform: translateX(-50%); /* Adjusts for the planet's width */
  animation: ${float} 10s infinite ease-in-out;
  box-shadow: 
    inset -30px -30px 50px rgba(0, 0, 0, 0.4),
    20px 20px 60px rgba(156, 179, 158, 0.1);
  overflow: hidden;
  z-index: 2; /* Ensures the planet is above the background but below the message */
  &:before {
    content: '';
    position: absolute;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      45deg,
      rgba(255, 255, 255, 0.1) 0%,
      rgba(255, 255, 255, 0.05) 50%,
      transparent 50%
    );
    transform: rotate(-45deg);
    top: -100%;
    left: -100%;
  }
  &:after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background: radial-gradient(
      circle at 70% 70%,
      rgba(255, 255, 255, 0.2) 0%,
      rgba(255, 255, 255, 0.1) 25%,
      transparent 50%
    );
  }
`;

const Asteroid = styled.div`
  width: 60px;
  height: 45px;
  background: linear-gradient(45deg, #95a5a6, #7f8c8d);
  border-radius: 50% 20% 50% 30%;
  position: absolute;
  top: 50%; /* Centers the asteroid vertically */
  left: -60px; /* Starts the asteroid just off-screen to the left */
  animation: ${asteroidMovement} 10s linear infinite;
  box-shadow: inset -5px -5px 15px rgba(0, 0, 0, 0.3);
`;

const ShootingStar = styled.div`
  position: absolute;
  width: 100px;
  height: 2px;
  background: linear-gradient(to left, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0));
  transform: rotate(45deg);
  animation: ${shootingStarAnimation} ${props => props.duration}s linear infinite;
  animation-delay: ${props => props.delay}s;
  &:before {
    content: '';
    position: absolute;
    top: 50%;
    right: 0;
    border: 2px solid transparent;
    border-left: 4px solid rgba(255, 255, 255, 0.8);
    transform: translateY(-50%);
  }
`;

const Message = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  color: #fff;
  z-index: 10;
`;

const Title = styled.h1`
  font-size: 8rem;
  margin: 0;
  text-shadow: 0 0 10px rgba(255, 255, 255, 0.7);
`;

const Subtitle = styled.p`
  font-size: 1.5rem;
  margin-top: 1rem;
`;

const HomeButton = styled(Link)`
  display: inline-block;
  margin-top: 2rem;
  padding: 12px 24px;
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  text-decoration: none;
  border-radius: 30px;
  border: 2px solid #fff;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(255, 255, 255, 0.2);
  }
`;

const NotFound = () => {
  const [stars, setStars] = useState([]);
  const [shootingStars, setShootingStars] = useState([]);

  const generateStars = useCallback(() => {
    const newStars = [];
    for (let i = 0; i < 200; i++) {
      newStars.push({
        id: i,
        x: Math.random() * window.innerWidth,
        y: Math.random() * window.innerHeight,
        size: Math.random() * 3 + 1,
        duration: Math.random() * 3 + 2
      });
    }
    setStars(newStars);
  }, []);

  const generateShootingStars = useCallback(() => {
    const newShootingStars = [];
    for (let i = 0; i < 2; i++) {  // Reduced to 2 shooting stars
      newShootingStars.push({
        id: i,
        top: Math.random() * (window.innerHeight / 2),  // Start in top half of screen
        left: Math.random() * window.innerWidth + (window.innerWidth / 2),  // Start in right half of screen
        duration: Math.random() * 3 + 2,
        delay: Math.random() * 15  // Increased delay for less frequent appearances
      });
    }
    setShootingStars(newShootingStars);
  }, []);

  useEffect(() => {
    generateStars();
    generateShootingStars();
    window.addEventListener('resize', generateStars);
    window.addEventListener('resize', generateShootingStars);
    return () => {
      window.removeEventListener('resize', generateStars);
      window.removeEventListener('resize', generateShootingStars);
    };
  }, [generateStars, generateShootingStars]);

  const handleMouseMove = useCallback((e) => {
    setStars(prevStars => prevStars.map(star => {
      const dx = e.clientX - star.x;
      const dy = e.clientY - star.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance < 100) {
        const angle = Math.atan2(dy, dx);
        const newX = star.x - Math.cos(angle) * (100 - distance) * 0.1;
        const newY = star.y - Math.sin(angle) * (100 - distance) * 0.1;
        return { ...star, x: newX, y: newY };
      }
      return star;
    }));
  }, []);

  return (
    <SpaceContainer onMouseMove={handleMouseMove}>
      {stars.map(star => (
        <Star
          key={star.id}
          style={{
            top: star.y,
            left: star.x,
            width: star.size,
            height: star.size
          }}
          duration={star.duration}
        />
      ))}
      {shootingStars.map(shootingStar => (
        <ShootingStar
          key={shootingStar.id}
          style={{
            top: shootingStar.top,
            left: shootingStar.left
          }}
          duration={shootingStar.duration}
          delay={shootingStar.delay}
        />
      ))}
      <Planet />
      <Asteroid />
      <Message>
        <Title>404</Title>
        <Subtitle>Oops! The sparkle you were looking for has gone missing.</Subtitle>
        <HomeButton to="/">Return to Earth</HomeButton>
      </Message>
    </SpaceContainer>
  );
};

export default NotFound;
